import { useEffect, useMemo, useState } from "react";
import { useEmployeeNameOptions } from "../../../hooks/use-employee-name-options.ts";
import { useEmployeeProfile } from "../../../hooks/use-employee-profile.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { Stepper } from "../../ui/index.ts";
import { LETTER_TYPE_OPTIONS } from "./letter-type-options.ts";
import { AnnexureLetterDetailsStep } from "./steps/annexure-letter-details-step.tsx";
import { BasicDetailsStep } from "./steps/basic-details-step.tsx";
import { createAppraisalDraftPayload } from "./appraisal-annexure-utils.ts";
import { useCreateAppraisalDraft } from "./use-create-appraisal-draft.ts";
import { API_APPRAISAL_V2 } from "../../../../config/Endpoints.js";
import { postApi, putApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { useDesignationOptions } from "./use-designation-options.ts";
import { useDepartmentOptions } from "../../../hooks/use-department-options.ts";
import {
  INITIAL_APPRAISAL_ANNEXURE_VALUES,
  INITIAL_EMPLOYEE_APPRAISAL_VALUES,
  type AppraisalAnnexureValues,
  type EmployeeAppraisalValues,
} from "./types.ts";
import styles from "./employee-appraisal.module.css";
import type { AppraisalDetail } from "../appraisal-workflow/types.ts";
import { appraisalAnnexureValuesFromDetail, appraisalBasicValuesFromDetail } from "./appraisal-prefill.ts";

const STEPS = [
  { id: "basic", label: "Basic Details" },
  { id: "annexure", label: "Annexure Letter Details" },
];

type EmployeeAppraisalProps = {
  initialAppraisal?: AppraisalDetail | null;
};

export function EmployeeAppraisal({ initialAppraisal = null }: EmployeeAppraisalProps) {
  const isResubmission = initialAppraisal?.status === "REJECTED";
  const employees = useEmployeeNameOptions();
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState<EmployeeAppraisalValues>(() => initialAppraisal ? appraisalBasicValuesFromDetail(initialAppraisal) : INITIAL_EMPLOYEE_APPRAISAL_VALUES);
  const [annexureValues, setAnnexureValues] = useState<AppraisalAnnexureValues>(() => initialAppraisal ? appraisalAnnexureValuesFromDetail(initialAppraisal) : INITIAL_APPRAISAL_ANNEXURE_VALUES);
  const draft = useCreateAppraisalDraft();
  const [appraisalId, setAppraisalId] = useState<number | null>(() => initialAppraisal?.appraisalId ?? initialAppraisal?.id ?? null);
  const [submitting, setSubmitting] = useState(false);
  const employeeId = values.employeeId ? Number(values.employeeId) : null;
  const profile = useEmployeeProfile(
    employeeId != null && Number.isFinite(employeeId) ? employeeId : null,
  );
  const departments = useDepartmentOptions();
  const selectedLetter = LETTER_TYPE_OPTIONS.find((option) => option.value === values.letterType) ?? LETTER_TYPE_OPTIONS[0];
  const designations = useDesignationOptions(selectedLetter.fields.designation ? values.departmentId : null);
  const designationOptions = useMemo(() => {
    if (!values.designationId || !values.designation || designations.options.some((option) => option.value === values.designationId)) {
      return designations.options;
    }
    return [{ value: values.designationId, label: values.designation }, ...designations.options];
  }, [designations.options, values.designation, values.designationId]);

  useEffect(() => { if (employees.error) showError(employees.error, { id: "appraisal-employees-load-error" }); }, [employees.error]);
  useEffect(() => { if (profile.error) showError(profile.error, { id: "appraisal-profile-load-error" }); }, [profile.error]);
  useEffect(() => { if (designations.error) showError(designations.error, { id: "appraisal-designations-load-error" }); }, [designations.error]);
  useEffect(() => { if (departments.error) showError(departments.error, { id: "appraisal-departments-load-error" }); }, [departments.error]);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      document.body.classList.remove("modal-open");
      if (document.body.style.overflow === "hidden") document.body.style.removeProperty("overflow");
      if (document.documentElement.style.overflow === "hidden") document.documentElement.style.removeProperty("overflow");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [stepIndex]);

  const selectedEmployeeName = useMemo(
    () => employees.options.find((option) => option.value === values.employeeId)?.label ?? profile.personalDetail?.empName ?? initialAppraisal?.employeeName ?? "",
    [employees.options, initialAppraisal?.employeeName, profile.personalDetail?.empName, values.employeeId],
  );

  const update = <K extends keyof EmployeeAppraisalValues>(key: K, value: EmployeeAppraisalValues[K]) => {
    if (key === "letterType" && value !== values.letterType) {
      setAnnexureValues(INITIAL_APPRAISAL_ANNEXURE_VALUES);
      if (!initialAppraisal) setAppraisalId(null);
    }
    setValues((current) => ({ ...current, [key]: value }));
  };

  const continueFromBasic = () => {
    if (!values.employeeId) {
      showError("Select an employee to continue.", { id: "appraisal-employee-required" });
      return;
    }
    if (!values.letterIssueDate || !values.effectiveDate) {
      showError("Letter issue date and effective date are required.", { id: "appraisal-dates-required" });
      return;
    }
    if (selectedLetter.fields.designation && !values.departmentId) {
      showError("Select a department to continue.", { id: "appraisal-department-required" });
      return;
    }
    if (selectedLetter.fields.designation && !values.designationId) {
      showError("Select a designation to continue.", { id: "appraisal-designation-required" });
      return;
    }
    if (selectedLetter.fields.designation && values.designation.trim().toLocaleLowerCase() === (profile.personalDetail?.designation?.trim().toLocaleLowerCase() ?? "")) {
      showError("The employee's current designation cannot be selected. Choose a different designation.", { id: "appraisal-current-designation-not-allowed" });
      return;
    }
    setStepIndex(1);
  };

  const reset = () => {
    setValues(INITIAL_EMPLOYEE_APPRAISAL_VALUES);
    setAnnexureValues(INITIAL_APPRAISAL_ANNEXURE_VALUES);
    setStepIndex(0);
    setAppraisalId(null);
  };

  const updateAnnexure = <K extends keyof AppraisalAnnexureValues>(key: K, value: AppraisalAnnexureValues[K]) => {
    setAnnexureValues((current) => ({ ...current, [key]: value }));
  };

  const salutation = initialAppraisal?.salutation ?? (profile.personalDetail?.gender?.toLowerCase() === "female" ? "Ms." : "Mr.");
  const saveDraft = async () => {
    if (!annexureValues.monthlyGross) {
      showError("Enter the monthly A+B+C total before saving the draft.", { id: "appraisal-monthly-gross-required" });
      return;
    }
    try {
      const response = await draft.saveDraft({
        ...createAppraisalDraftPayload(values, annexureValues, salutation),
        status: "DRAFT",
      }, appraisalId);
      const savedId = response.data?.appraisalId;
      if (savedId) setAppraisalId(savedId);
      showSuccess(response.message ?? "Appraisal draft created successfully.");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to save appraisal draft.", { id: "appraisal-save-draft-error" });
    }
  };

  const sendForApproval = async () => {
    const annualFixedComponent = Number(annexureValues.annualComponentAmount);
    if (annexureValues.annualComponentEnabled && (!Number.isFinite(annualFixedComponent) || annualFixedComponent <= 0)) {
      showError("Annual Fixed Component must be greater than 0 when Annual Component is Yes.", { id: "appraisal-annual-fixed-component-required" });
      return;
    }
    if (!annexureValues.monthlyGross) {
      showError("Enter the monthly A+B+C total before submitting for approval.", { id: "appraisal-submit-monthly-gross-required" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...createAppraisalDraftPayload(values, annexureValues, salutation),
        appraisalId,
      };
      if (isResubmission) {
        if (!appraisalId) throw new Error("The appraisal ID is missing. Reopen the appraisal and try again.");
        const { data } = await putApi<{ message?: string }>(`${API_APPRAISAL_V2}/${appraisalId}`, accessToken(), payload);
        showSuccess(data.message ?? "Appraisal updated and resubmitted successfully.");
      } else {
        const { data } = await postApi<{ message?: string }>(`${API_APPRAISAL_V2}/submit`, accessToken(), payload);
        showSuccess(data.message ?? "Appraisal submitted for approval.");
      }
      reset();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to submit appraisal.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateLetterIssueDate = (date: Date | null) => {
    setValues((current) => ({
      ...current,
      letterIssueDate: date,
    }));
  };

  return <div className={styles.page}>
    <Stepper steps={STEPS} currentStepIndex={stepIndex} aria-label="Employee appraisal letter progress" />

    {stepIndex === 0 ? <BasicDetailsStep values={values} employeeOptions={employees.options} employeesLoading={employees.loading} selectedEmployeeName={selectedEmployeeName} personalDetail={profile.personalDetail} profileLoading={profile.loading} departmentOptions={departments.options} departmentsLoading={departments.loading} designationOptions={designationOptions} designationsLoading={designations.loading} onChange={update} onLetterIssueDateChange={updateLetterIssueDate} onReset={reset} onContinue={continueFromBasic} /> : null}

    {stepIndex === 1 ? <AnnexureLetterDetailsStep values={annexureValues} basicValues={values} saving={draft.loading} submitting={submitting} onChange={updateAnnexure} onBack={() => setStepIndex(0)} onSaveDraft={() => void saveDraft()} onSubmitForApproval={() => void sendForApproval()} /> : null}
  </div>;
}
